import React, { useCallback, useEffect, useRef, useState } from "react";

import {
  AlertCircle,
  ArrowLeft,
  ArrowRight,
  Check,
  ImageIcon,
  Loader2,
  Trash2,
} from "lucide-react";
import { useTranslation } from "react-i18next";

import Toast from "../../../common/Toast/Toast";
import EntityConstants from "../../../configs/EntityConstants";
import { useSearchDebounce } from "../../../hooks/useSearchDebounce";
import { cn } from "../../../lib/utils";
import { GET, POST } from "../../../service/CRUDService";
import { automationTestIds } from "../../../testing/automationTestIds";
import convertToBase64 from "../../../utils/ConvertToBase64";
import CountrySelect from "../components/CountrySelect";
import LiveCertificatePreview from "../components/LiveCertificatePreview";

const INPUT_CLASS =
  "w-full !rounded-[8px] border-[1px] bg-cfSecondary px-3.5 py-2.5 text-sm text-cfHeadingText placeholder:text-cfSignInSignUpInputPlaceholderText focus:outline-none transition-[border-color,box-shadow] duration-150";
const INPUT_OK =
  "!border-cfSignInSignUpInputBorder focus:!border-cfPrimaryButton focus:!shadow-[0_0_0_3px_color-mix(in_srgb,var(--cf-primary-color)_18%,transparent)]";
const INPUT_ERROR =
  "!border-cfErrorText focus:!shadow-[0_0_0_3px_color-mix(in_srgb,var(--cf-error-text-color)_16%,transparent)]";
const LABEL_CLASS =
  "block text-sm font-medium text-cfSignInSignUpInputLabelText mb-1.5";

const RequiredMark = () => (
  <span aria-hidden="true" className="ml-0.5 text-cfErrorText">
    *
  </span>
);

const ACCEPTED_EXTENSIONS = ["png", "jpg", "jpeg", "svg"];
const ACCEPTED_MIME_TYPES = [
  "image/png",
  "image/jpeg",
  "image/jpg",
  "image/svg+xml",
];
const MAX_SIZE_BYTES = 2 * 1024 * 1024;

const OrganisationLogoUpload = ({
  previewSrc,
  onChange,
  onRemove,
  disabled,
  className,
}) => {
  const { t } = useTranslation(["onboarding"]);
  const inputRef = useRef(null);
  const [dragActive, setDragActive] = useState(false);

  const openPicker = () => {
    if (!disabled) {
      inputRef.current?.click();
    }
  };

  const processFile = async (file) => {
    if (!file || disabled) {
      return;
    }

    const extension = file.name.split(".").pop()?.toLowerCase();
    const hasValidExtension = Boolean(
      extension && ACCEPTED_EXTENSIONS.includes(extension),
    );
    const hasValidMime =
      !file.type || ACCEPTED_MIME_TYPES.includes(file.type.toLowerCase());

    if (!hasValidExtension || !hasValidMime) {
      Toast({
        type: "error",
        title: t("onboarding:profile_save_error_title"),
        message: t("onboarding:profile_logo_format_error"),
      });
      return;
    }

    if (file.size > MAX_SIZE_BYTES) {
      Toast({
        type: "error",
        title: t("onboarding:profile_save_error_title"),
        message: t("onboarding:profile_logo_size_error"),
      });
      return;
    }

    try {
      onChange(await convertToBase64(file));
    } catch {
      Toast({
        type: "error",
        title: t("onboarding:profile_save_error_title"),
        message: t("onboarding:profile_logo_read_error"),
      });
    }
  };

  const handleDrop = (event) => {
    event.preventDefault();
    setDragActive(false);
    processFile(event.dataTransfer.files?.[0]);
  };

  return (
    <div
      onDragOver={(event) => {
        event.preventDefault();
        if (!disabled) setDragActive(true);
      }}
      onDragLeave={(event) => {
        if (!event.currentTarget.contains(event.relatedTarget)) {
          setDragActive(false);
        }
      }}
      onDrop={handleDrop}
      className={cn(
        "flex flex-col items-center justify-center rounded-xl border-[1px] border-dashed p-6 text-center transition-colors",
        dragActive
          ? "border-cfPrimaryButton bg-[color-mix(in_srgb,var(--cf-primary-color)_6%,white)]"
          : "border-cfSignInSignUpInputBorder bg-cfSecondary",
        disabled && "opacity-60",
        className,
      )}
    >
      <input
        ref={inputRef}
        type="file"
        id={automationTestIds.onboardingLogoUpload}
        className="hidden"
        accept={ACCEPTED_EXTENSIONS.map((ext) => `.${ext}`)
          .concat(ACCEPTED_MIME_TYPES)
          .join(",")}
        disabled={disabled}
        onChange={(event) => {
          const file = event.target.files?.[0];
          event.target.value = "";
          processFile(file);
        }}
      />

      {previewSrc ? (
        <div className="flex min-h-0 w-full flex-1 flex-col items-center gap-4">
          <div
            className="relative min-h-[11rem] w-full flex-1 overflow-hidden rounded-[10px] border-[1px] border-cfCardBorder"
            style={{
              backgroundColor:
                "color-mix(in srgb, var(--cf-primary-color) 4%, white)",
            }}
          >
            <img
              src={previewSrc}
              alt={t("onboarding:profile_logo_alt")}
              className="absolute inset-0 h-full w-full object-contain"
              style={{ padding: 16 }}
            />
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={openPicker}
              disabled={disabled}
              className="rounded-[8px] border-[1px] border-cfSignInSignUpInputBorder bg-white px-4 py-2 text-sm font-medium text-cfHeadingText transition-colors hover:bg-cfTableHeaderBackgroundColor focus:outline-none focus-visible:ring-2 focus-visible:ring-cfPrimaryButton focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-60"
            >
              {t("onboarding:profile_logo_replace")}
            </button>
            {onRemove ? (
              <button
                type="button"
                id={automationTestIds.onboardingLogoRemove}
                onClick={onRemove}
                disabled={disabled}
                className="inline-flex items-center gap-1.5 rounded-[8px] px-3 py-2 text-sm font-medium text-cfGlobalText transition-colors hover:text-cfErrorText focus:outline-none focus-visible:ring-2 focus-visible:ring-cfPrimaryButton focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-60"
              >
                <Trash2 className="h-4 w-4" aria-hidden="true" />
                {t("onboarding:profile_logo_remove")}
              </button>
            ) : null}
          </div>
        </div>
      ) : (
        <div className="flex flex-col items-center gap-3">
          <span className="flex h-12 w-12 items-center justify-center rounded-full bg-cfPrimaryMuted">
            <ImageIcon
              className="h-5 w-5 text-cfPrimary"
              strokeWidth={1.75}
              aria-hidden="true"
            />
          </span>
          <div>
            <p className="text-sm text-cfGlobalText">
              {t("onboarding:profile_logo_heading")}
            </p>
            <p className="mt-1 text-xs text-cfTextBreadcrumbDisabled">
              {t("onboarding:profile_logo_hint")}
            </p>
          </div>
          <button
            type="button"
            onClick={openPicker}
            disabled={disabled}
            className="rounded-[8px] border-[1px] border-cfSignInSignUpInputBorder bg-white px-4 py-2 text-sm font-medium text-cfPrimaryButton transition-colors hover:bg-[color-mix(in_srgb,var(--cf-primary-color)_8%,white)] focus:outline-none focus-visible:ring-2 focus-visible:ring-cfPrimaryButton focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-60"
          >
            {t("onboarding:profile_logo_choose")}
          </button>
        </div>
      )}
    </div>
  );
};

const DOMAIN_AVAILABILITY = {
  AVAILABLE: "available",
  TAKEN: "taken",
  INVALID: "invalid",
  UNKNOWN: "unknown",
};

const IDLE_DOMAIN_CHECK = {
  status: DOMAIN_AVAILABILITY.UNKNOWN,
  isChecking: false,
};

export const ORG_PROFILE_DRAFT_KEY = "onboarding_org_profile_draft";

const readStoredDraft = () => {
  try {
    const raw = sessionStorage.getItem(ORG_PROFILE_DRAFT_KEY);
    if (!raw) {
      return null;
    }
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === "object" ? parsed : null;
  } catch {
    return null;
  }
};

const writeStoredDraft = (draft) => {
  try {
    sessionStorage.setItem(ORG_PROFILE_DRAFT_KEY, JSON.stringify(draft));
  } catch {
    // Private mode can refuse storage; the step still works without it.
  }
};

const OrganisationProfileStep = ({ onComplete, onBack }) => {
  const { t } = useTranslation(["onboarding"]);

  const [organisationName, setOrganisationName] = useState("");
  const [country, setCountry] = useState("");
  const [domainInput, setDomainInput] = useState("");
  const [initialDomain, setInitialDomain] = useState("");
  const [existingLogo, setExistingLogo] = useState("");
  const [logoUpload, setLogoUpload] = useState("");

  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [fieldErrors, setFieldErrors] = useState({});
  const [formError, setFormError] = useState("");

  const [domainCheck, setDomainCheck] = useState(IDLE_DOMAIN_CHECK);
  const latestDomainRef = useRef("");

  const checkDomain = useCallback(async (domain, signal) => {
    let result = { status: DOMAIN_AVAILABILITY.UNKNOWN };
    try {
      const response = await GET(
        `${EntityConstants.GET_ORG_DOMAIN_AVAILABILITY}?org_domain=${encodeURIComponent(domain)}`,
        { signal },
      );
      if (response) {
        const payload = await response.json().catch(() => null);
        const data = payload?.data ?? payload;
        if (
          payload?.error_code === "invalid_org_domain" ||
          payload?.error_code === "system_domain"
        ) {
          result = {
            status: DOMAIN_AVAILABILITY.INVALID,
            errorCode: payload.error_code,
            message: payload.error || payload.message || "",
          };
        } else if (data?.available === true) {
          result = { status: DOMAIN_AVAILABILITY.AVAILABLE };
        } else if (
          data?.available === false ||
          payload?.domain_exists === true
        ) {
          result = {
            status: DOMAIN_AVAILABILITY.TAKEN,
            message: payload?.message || "",
          };
        }
      }
    } catch (error) {
      if (error?.name === "AbortError") {
        return;
      }
      console.error(error);
    }
    if (latestDomainRef.current === domain) {
      setDomainCheck({ ...result, isChecking: false });
    }
  }, []);

  const { handleSearch } = useSearchDebounce(checkDomain, 500);

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      let details = null;
      try {
        const response = await GET(EntityConstants.GET_ORG_DETAILS);
        if (response?.ok) {
          details = await response.json();
        }
      } catch (error) {
        console.error(error);
      }
      if (cancelled) {
        return;
      }

      const draft = readStoredDraft();
      if (details && typeof details === "object") {
        // GET still returns a generated name for navbar/settings. New
        // onboarding should type the real organisation name, so leave empty
        // unless the user already typed one on this step.
        setOrganisationName(draft?.organisationName || "");
        setCountry(draft?.country || details.country || "");
        setDomainInput(draft?.domainInput || details.org_domain || "");
        setInitialDomain((details.org_domain || "").trim());
        setExistingLogo(details.org_logo || "");
      } else if (draft) {
        setOrganisationName(draft.organisationName || "");
        setCountry(draft.country || "");
        setDomainInput(draft.domainInput || "");
      }
      setIsLoading(false);
    };

    load();
    return () => {
      cancelled = true;
    };
  }, []);

  const trimmedName = organisationName.trim();
  const trimmedDomain = domainInput.trim();
  const isDomainBlocking =
    domainCheck.status === DOMAIN_AVAILABILITY.TAKEN ||
    domainCheck.status === DOMAIN_AVAILABILITY.INVALID;

  const canSave =
    Boolean(trimmedName) && Boolean(country) && !isDomainBlocking && !isSaving;

  const persistDraft = (next) => {
    writeStoredDraft({
      organisationName,
      country,
      domainInput,
      ...next,
    });
  };

  const handleDomainChange = (value) => {
    const domain = value.trim();
    latestDomainRef.current = domain;
    setDomainInput(value);
    persistDraft({ domainInput: value });
    setDomainCheck(
      domain ? { ...IDLE_DOMAIN_CHECK, isChecking: true } : IDLE_DOMAIN_CHECK,
    );
    handleSearch(domain, {
      minChars: 1,
      onError: () => setDomainCheck(IDLE_DOMAIN_CHECK),
    });
  };

  const handleSave = async () => {
    if (isSaving) {
      return;
    }

    const errors = {};
    if (!trimmedName) {
      errors.organisation_name = t("onboarding:profile_org_name_required");
    }
    if (!country) {
      errors.country = t("onboarding:profile_country_required");
    }
    if (Object.keys(errors).length) {
      setFieldErrors(errors);
      setFormError("");
      return;
    }

    setFieldErrors({});
    setFormError("");
    setIsSaving(true);

    const payload = { organisation_name: trimmedName, country };
    if (trimmedDomain && trimmedDomain !== initialDomain) {
      payload.org_domain = trimmedDomain;
    }
    if (logoUpload) {
      payload.org_logo = logoUpload;
    }

    let message = "";
    let saved = false;
    try {
      const response = await POST(EntityConstants.POST_ORG_DETAILS, payload);
      if (response?.ok) {
        saved = true;
      } else if (response) {
        const errorData = await response.json().catch(() => null);
        message = []
          .concat(errorData?.message ?? [])
          .filter((text) => typeof text === "string")
          .join(" ");
      }
    } catch (error) {
      console.error(error);
    }
    setIsSaving(false);

    if (saved) {
      onComplete();
      return;
    }

    message = message || t("onboarding:profile_save_error_message");
    setFormError(message);
    Toast({
      type: "error",
      title: t("onboarding:profile_save_error_title"),
      message,
    });
  };

  const domainHint = (() => {
    if (!trimmedDomain) {
      return { tone: "muted", text: t("onboarding:profile_domain_hint") };
    }
    if (domainCheck.isChecking) {
      return { tone: "muted", text: t("onboarding:profile_domain_checking") };
    }
    if (domainCheck.status === DOMAIN_AVAILABILITY.TAKEN) {
      return {
        tone: "error",
        text: domainCheck.message || t("onboarding:profile_domain_taken"),
      };
    }
    if (domainCheck.status === DOMAIN_AVAILABILITY.INVALID) {
      return {
        tone: "error",
        text:
          domainCheck.message ||
          (domainCheck.errorCode === "system_domain"
            ? t("onboarding:profile_domain_reserved")
            : t("onboarding:profile_domain_invalid")),
      };
    }
    if (domainCheck.status === DOMAIN_AVAILABILITY.AVAILABLE) {
      return {
        tone: "success",
        text: t("onboarding:profile_domain_available"),
      };
    }
    return { tone: "muted", text: t("onboarding:profile_domain_hint") };
  })();

  if (isLoading) {
    return (
      <div
        role="status"
        aria-live="polite"
        aria-busy="true"
        className="grid grid-cols-1 gap-8 lg:grid-cols-2"
      >
        <div className="space-y-5">
          <div className="h-10 w-full animate-pulse rounded-lg bg-gray-200" />
          <div className="h-10 w-full animate-pulse rounded-lg bg-gray-200" />
          <div className="h-32 w-full animate-pulse rounded-xl bg-gray-200" />
        </div>
        <div className="aspect-[1.414] w-full animate-pulse rounded-2xl bg-gray-200" />
        <span className="sr-only">{t("onboarding:loading")}</span>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-8">
      <div className="grid grid-cols-1 items-stretch gap-8 rounded-2xl border-[1px] border-cfCardBorder bg-cfSecondary p-[20px] shadow-[0_1px_2px_rgba(16,24,40,0.05)] sm:p-6 lg:grid-cols-[minmax(0,1fr)_minmax(0,1fr)] xl:grid-cols-[minmax(0,1fr)_minmax(0,1.05fr)]">
        <div className="flex flex-col gap-5 lg:h-full">
          <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-1 xl:grid-cols-2">
            <div>
              <label
                className={LABEL_CLASS}
                htmlFor={automationTestIds.onboardingOrgNameInput}
              >
                {t("onboarding:profile_org_name_label")}
                <RequiredMark />
              </label>
              <input
                id={automationTestIds.onboardingOrgNameInput}
                type="text"
                value={organisationName}
                disabled={isSaving}
                placeholder={t("onboarding:profile_org_name_placeholder")}
                aria-invalid={Boolean(fieldErrors.organisation_name)}
                aria-describedby={
                  fieldErrors.organisation_name
                    ? `${automationTestIds.onboardingOrgNameInput}-error`
                    : undefined
                }
                onChange={(event) => {
                  const value = event.target.value;
                  setOrganisationName(value);
                  persistDraft({ organisationName: value });
                  setFieldErrors((prev) => ({
                    ...prev,
                    organisation_name: undefined,
                  }));
                }}
                className={cn(
                  INPUT_CLASS,
                  fieldErrors.organisation_name ? INPUT_ERROR : INPUT_OK,
                )}
              />
              {fieldErrors.organisation_name ? (
                <p
                  id={`${automationTestIds.onboardingOrgNameInput}-error`}
                  className="mt-1.5 text-xs text-cfErrorText"
                >
                  {fieldErrors.organisation_name}
                </p>
              ) : null}
            </div>

            <div>
              <label
                className={LABEL_CLASS}
                htmlFor={automationTestIds.onboardingCountrySelect}
              >
                {t("onboarding:profile_country_label")}
                <RequiredMark />
              </label>
              <CountrySelect
                id={automationTestIds.onboardingCountrySelect}
                value={country}
                disabled={isSaving}
                invalid={Boolean(fieldErrors.country)}
                describedBy={
                  fieldErrors.country
                    ? `${automationTestIds.onboardingCountrySelect}-error`
                    : undefined
                }
                onChange={(value) => {
                  setCountry(value);
                  persistDraft({ country: value });
                  setFieldErrors((prev) => ({ ...prev, country: undefined }));
                }}
              />
              {fieldErrors.country ? (
                <p
                  id={`${automationTestIds.onboardingCountrySelect}-error`}
                  className="mt-1.5 text-xs text-cfErrorText"
                >
                  {fieldErrors.country}
                </p>
              ) : null}
            </div>
          </div>

          <div>
            <label
              className={LABEL_CLASS}
              htmlFor={automationTestIds.onboardingDomainInput}
            >
              {t("onboarding:profile_domain_label")}
            </label>
            <div className="relative">
              <input
                id={automationTestIds.onboardingDomainInput}
                type="text"
                value={domainInput}
                disabled={isSaving}
                placeholder={t("onboarding:profile_domain_placeholder")}
                aria-invalid={domainHint.tone === "error"}
                aria-describedby={automationTestIds.onboardingDomainStatus}
                onChange={(event) => handleDomainChange(event.target.value)}
                className={cn(
                  INPUT_CLASS,
                  "pr-10",
                  domainHint.tone === "error" ? INPUT_ERROR : INPUT_OK,
                )}
              />
              <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2">
                {domainCheck.isChecking ? (
                  <Loader2
                    className="h-4 w-4 animate-spin text-cfTextBreadcrumbDisabled"
                    aria-hidden="true"
                  />
                ) : null}
                {!domainCheck.isChecking && domainHint.tone === "success" ? (
                  <Check
                    className="h-4 w-4 text-cfActiveText"
                    aria-hidden="true"
                  />
                ) : null}
                {!domainCheck.isChecking && domainHint.tone === "error" ? (
                  <AlertCircle
                    className="h-4 w-4 text-cfErrorText"
                    aria-hidden="true"
                  />
                ) : null}
              </span>
            </div>
            <p
              id={automationTestIds.onboardingDomainStatus}
              aria-live="polite"
              className={cn(
                "mt-1.5 text-xs",
                domainHint.tone === "error" && "text-cfErrorText",
                domainHint.tone === "success" && "text-cfActiveText",
                domainHint.tone === "muted" && "text-cfTextBreadcrumbDisabled",
              )}
            >
              {domainHint.text}
            </p>
          </div>

          <div className="flex min-h-0 flex-1 flex-col">
            <span className={LABEL_CLASS}>
              {t("onboarding:profile_logo_label")}
            </span>
            <OrganisationLogoUpload
              previewSrc={logoUpload || existingLogo}
              disabled={isSaving}
              onChange={setLogoUpload}
              onRemove={logoUpload ? () => setLogoUpload("") : undefined}
              className="min-h-[10.5rem] flex-1"
            />
          </div>
        </div>

        <div className="lg:sticky lg:top-[6.5rem]">
          <LiveCertificatePreview
            organisationName={organisationName}
            country={country}
            domain={trimmedDomain}
            logoSrc={logoUpload || existingLogo}
          />
        </div>
      </div>

      <div className="flex flex-col gap-3 border-t border-cfCardBorder pt-6 sm:flex-row sm:items-center sm:justify-end">
        {onBack ? (
          <button
            type="button"
            id={automationTestIds.onboardingProfileBack}
            onClick={onBack}
            disabled={isSaving}
            className="inline-flex items-center gap-2 px-4 py-2.5 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed sm:mr-auto"
          >
            <ArrowLeft className="h-4 w-4" aria-hidden="true" />
            {t("onboarding:back")}
          </button>
        ) : null}
        {formError ? (
          <p
            id={automationTestIds.onboardingProfileFormError}
            role="alert"
            className={cn(
              "text-sm text-cfErrorText",
              !onBack && "sm:mr-auto",
            )}
          >
            {formError}
          </p>
        ) : null}
        <button
          type="button"
          id={automationTestIds.onboardingProfileSave}
          onClick={handleSave}
          disabled={!canSave}
          className="inline-flex min-w-[9rem] items-center justify-center gap-2 whitespace-nowrap rounded-[8px] bg-cfPrimaryButton px-5 py-2.5 text-sm font-medium text-white transition-colors hover:bg-cfPrimaryDark focus:outline-none focus-visible:ring-2 focus-visible:ring-cfPrimaryButton focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:bg-cfDisabled"
        >
          {isSaving ? (
            <>
              <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
              {t("onboarding:profile_saving")}
            </>
          ) : (
            <>
              {t("onboarding:profile_save")}
              <ArrowRight className="h-4 w-4" aria-hidden="true" />
            </>
          )}
        </button>
      </div>
    </div>
  );
};

export default OrganisationProfileStep;
